import React from 'react';
import ClientConfig from '../../context/clientConfig';
import DefaultLogo from '../../assets/placeholderLogo.png';

const withTemplate = (PassedComponent: Function, PassedProps: Object) => {
  class WithTemplate extends React.Component<Props> {
    /**
     * @namespace
     * @property {string}       balanceFormatLocaleOverride         - Overrides the balance number formatting detected from returned value, requestedCurrency. (This does NOT affect the currency symbol) 
     * @property {string}       buttonAriaLabel                     - Aria Label to apply to form button
     * @property {string}       buttonClassNames                    - CSS Classes to apply to form buttons
     * @property {string}       buttonText                          - Text to display within form buttons
     * @property {string}       captchaLabelText                    - Label text for Google reCaptcha
     * @property {string}       cardClassNames                      - CSS Classes to apply to card image
     * @property {string}       cardErrorMsg                        - Error Message to display under Card Number Input field when input is invalid 
     * @property {string}       cardInputLabel                      - Label text for Card Number input field
     * @property {string}       cardInputPlaceholder                - Placeholder text for Card Number input field
     * @property {string}       cardInputRequiredMark               - Character to indicate Card Number input field is required
     * @property {object}       cardLabelTooltipComponent           - Custom Tooltip component to pop-out when Card Number input is hovered over
     * @property {string|array} cardLength                          - String or Array to represent allowed card input length or lengths to be entered by user  
     * @property {boolean}      clearCardNumberOnError              - Clears out card number input if form submission results in an Error
     * @property {boolean}      clearCardNumberOnSuccess            - Clears out card number input if form submission results in Success
     * @property {string}       clientBodyClassNames                - CSS Classes to apply to client-body container
     * @property {string}       clientContentClassNames             - CSS Classes to apply to client-content container
     * @property {string}       clientName                          - Name of Client to appear in No Access Message
     * @property {string}       currencySymbolFormatOverride        - Overrides the currencyDisplay option in the IntlNumberFormat JS library, see docs for all options
     * @property {string}       customCardMessage                   - Custom message including card number above the balance
     * @property {string}       customCardMessageClassNames         - Class Names for custom card message element
     * @property {string}       customNS                            - Custom i18n translation namespace to use for client
     * @property {string}       dateErrorMsg                        - Translation for the error message when a card's EOV or Transaction History Date is not able to be displayed
     * @property {number}       defaultEnhancedPageSize             - Default selected page size for page size options of Transaction History Table
     * @property {string}       facebookLink                        - URL to client's Facebook Page
     * @property {string}       footerClassNames                    - CSS Classes to apply to html container for page footer
     * @property {object}       footerComponent                     - Custom Component to use for Footer of page
     * @property {object}       formatCardNum                       - Configuration for special formatting of typed card number input
     * @property {number}       formatCardNum.digitsBetweenSpaces   - For spaced out card numbers, specify how many digits to allow before inserting a space
     * @property {boolean}      formatCardNum.enabled               - Allow typed card number to be custom formatted
     * @property {object}       formExtraComponent                  - Custom Component to show above/below the form; Need to use order CSS attribute to change location
     * @property {string}       gpayImageVariant                    - Specify variant of Google Pay badge to display
     * @property {string}       header                              - Sets which component to use for header of page
     * @property {string}       headingClassNames                   - CSS Classes to apply to headingText
     * @property {object}       headerComponent                     - Custom component to be used for header
     * @property {string}       headerLogoClassNames                - CSS Classes to apply to Form Header
     * @property {string}       headerImageAlt                      - Alt Text for header image
     * @property {string}       headerImage                         - Path to image used for Header (typically a banner)
     * @property {string}       headerImageClassNames               - CSS Classes to apply to image used within Header
     * @property {string}       headingOrder                        - CSS Order attribute to apply to Heading component
     * @property {string}       headingText                         - Text to display within Header
     * @property {string}       iconClassNames                      - CSS Classes to apply to icon
     * @property {string}       iconOrder                           - CSS Order attribute to apply to icon div
     * @property {string}       inputClassNames                     - CSS Classes to apply to form input controls
     * @property {string}       instagramLink                       - URL to client's Instagram Page
     * @property {boolean}      isIframe                            - Sets whether this page will ONLY be used in an iFrame
     * @property {string}       justifyContent                      - Sets justification of main form content
     * @property {string}       justifyLogo                         - Sets justification for logo at top of page
     * @property {string}       justifyFooter                       - Sets justification of footer content
     * @property {string}       linkedinLink                        - URL to client's LinkedIn Page
     * @property {string}       logoClassNames                      - CSS Classes to apply to Logo Image
     * @property {string}       logoImgAlt                          - Alt Text for Logo Image
     * @property {string}       logoLink                            - Link for Logo Image
     * @property {string}       logoURI                             - Path to image used for logo
     * @property {boolean}      labelAnimation                      - CSS Animation to apply to form labels
     * @property {string}       labelClassNames                     - CSS Classes to apply to form labels
     * @property {string}       langOverride                        - Overrides detected Browser language
     * @property {object}       paddingConfig                       - Default values for configuring padding to be applied to an entered card number
     * @property {string}       paddingConfig.paddingCharacter      - Character to repeat as needed to apply desired padding
     * @property {number}       paddingConfig.padUpToLength         - Target length to pad entered card number up to
     * @property {string}       pinErrorMsg                         - Error Message to display under PIN Input field when input is invalid
     * @property {string}       pinInputLabel                       - Label text for PIN input field
     * @property {string}       pinInputPlaceholder                 - Placeholder text for PIN input field
     * @property {string}       pinInputRequiredMark                - Character to indicate PIN input field is required
     * @property {object}       pinLabelTooltipComponent            - Custom Tooltip component to pop-out when PIN input is hovered over
     * @property {string|array} pinLength                           - String or Array to represent allowed pin input length or lengths to be entered by user
     * @property {string}       pinterestLink                       - URL to client's Pinterest Page 
     * @property {string}       pinTruncate                         - String to configure which digits of the PIN to send; Used for PINS with a length > 4 
     * @property {string}       pinType                             - String to configure if PIN will be all numbers or alphanumeric?
     * @property {object}       prefixConfig                        - Default values for configuring prefix to be applied to an entered card number 
     * @property {object}       prefixConfig.lengthWithPrefix       - Max length possible for a prefixed card number. This is used to determine if a card number was entered with a prefix by a user
     * @property {string}       prefixConfig.prefixString           - String to be applied as a prefix
     * @property {array}        prefixConfig.exemptLengths          - Array of card lengths that, if entered, should not have a prefix applied
     * @property {string}       resultsClassNames                   - CSS Classes to apply to html container of the Balance Results
     * @property {string}       resultsExpiryText                   - Represents the label which accompanies the returned expiration date in the Results section
     * @property {string}       resultsHeadingClassNames            - CSS Classes to apply to heading of Balance Results section
     * @property {string}       resultsHeadingText                  - Represents the label which accompanies the returned balance in the Results section
     * @property {string}       resultsOrder                        - CSS Order attribute to apply to Results component
     * @property {object}       seperateResConfirmButton            - Custom Component for the Confirmation button on seperate results screen
     * @property {string}       seperateResultsMessage              - Message to display on alongside balance results on seperate screen
     * @property {string}       seperateResultsMessageRowClass      - CSS Classes to apply to seperate results screen message div
     * @property {string}       seperateResultsMessageURL           - Custom link to show above balance results, on seperate page
     * @property {boolean}      showCardErrorMsgOnBlank             - When the card input field is blank, should the validation error message be shown?
     * @property {boolean}      showCardEOV                         - Show EOV date alongside returned Balance
     * @property {boolean}      showCardNumber                      - Show last 4 digits of submitted Card Number alongside returned Balance
     * @property {boolean}      showCardNumCounter                  - Show counter indicating number of digits typed in card input counter
     * @property {boolean}      showCustomCardMessage               - Show custom message including card number above the balance
     * @property {boolean}      showEnhancedResults                 - Boolean to show the Transaction History Table alongside the returned balance 
     * @property {boolean}      showEnhancedStore                   - Boolean to show the Store column within the Transaction History Table
     * @property {boolean}      showFullCardNumber                  - Show all digits of submitted Card Number alongside returned Balance
     * @property {boolean}      showFooter                          - Sets whether to show footer at bottom of page
     * @property {boolean}      showIcon                            - Sets whether to show icon above header
     * @property {boolean}      showLabels                          - Sets whether to display labels on form inputs
     * @property {boolean}      showPageSizeOptions                 - Show options for number of results per page in the Transaction History Table
     * @property {boolean}      showPinErrorMsgOnBlank              - When the pin input field is blank, should the validation error message be
     * @property {boolean}      showPinNumCounter                   - Show counter indicating number of digits typed in the pin input counter
     * @property {boolean}      showSeperateResultsView             - Display Results on a fresh, seperate screen from the form
     * @property {boolean}      showTimeZone                        - Display time zone with transaction time
     * @property {boolean}      showTransactionTime                 - Show time of Balance Inquiry submission alongside returned Balance
     * @property {boolean}      sidePanel                           - Sets whether to show SidePanel menu component on page
     * @property {string}       spinnerOrder                        - CSS Order attribute to apply to Spinner component
     * @property {string}       trnTableDateFormatLocaleOverride    - Overrides the locale used to format transaction dates in enhanced results table
     * @property {string}       twitterLink                         - URL to client's Twitter Page
     * @property {string}       youtubeLink                         - URL to client's Youtube Page
     * @property {string}       withGiftCardFormContainerClassNames - CSS Classes to apply to form div within withGiftCard.js
    **/
    static defaultProps = {
      balanceFormatLocaleOverride: null,
      buttonAriaLabel: 'Check balance button',
      buttonClassNames: 'btn btn-secondary mt-2 mb-4',
      buttonText: 'Check Balance',
      captchaLabelText: '',
      cardClassNames: 'bottom-shadow-card rounded-0',
      cardErrorMsg: 'Please enter a $1 digit number.',
      cardInputLabel: 'Card Number',
      cardInputPlaceholder: '',
      cardInputRequiredMark: null,
      cardLabelTooltipComponent: null,
      cardLength: '19', // default "19", other custom values --> "13", [7, 13], [5, 10]
      clearCardNumberOnError: true,
      clearCardNumberOnSuccess: true,
      clientBodyClassNames: 'client-body-default',
      clientContentClassNames: 'col-sm-12 col-md-6 col-lg-4',
      clientName: '',
      currencySymbolFormatOverride: 'symbol',
      customCardMessage: '',
      customCardMessageClassNames: '',
      customNS: '',
      dateErrorMsg: 'date.error.msg',
      defaultEnhancedPageSize: 10,
      facebookLink: '',
      footerClassNames: 'footer-default',
      footerComponent: null,
      formatCardNum: { 
        enabled: false, 
        digitsBetweenSpaces: 0 
      },
      formExtraComponent: null,
      gpayImageVariant: 'DARK',
      header: 'none', // withLogo or withImage or none
      headerComponent: null, // overides the preconfigured header component
      headerImage: '',
      headerImageAlt: 'Header',
      headerImageClassNames: 'col-lg-6',
      headerLogoClassNames: 'bottom-shadow header-default',
      headingClassNames: 'headingOne roboto light-grey-text align-self-center',
      headingOrder: '', // by default natural order
      headingText: 'Balance Check',
      iconClassNames: 'fas fa-4x fa-gifts align-self-center icon',
      iconOrder: '', // by default natural order
      inputClassNames: '',
      instagramLink: '',
      isIframe: false, // is the page going to be used solely within an iFrame?
      justifyContent: 'center',
      justifyFooter: '', // start or center or end
      justifyLogo: 'start', // start or center or end
      labelAnimation: true,
      labelClassNames: '',
      langOverride: null,
      linkedinLink: '',
      logoClassNames: 'formatLogo',
      logoImgAlt: 'Brand',
      logoLink: 'https://www.storedvalue.com/en-US/home',
      logoURI: DefaultLogo,
      paddingConfig: {
        paddingCharacter: '',
        padUpToLength: null,
      },
      pinErrorMsg: 'Please enter a $1 digit number.',
      pinInputLabel: 'PIN',
      pinInputPlaceholder: '',
      pinInputRequiredMark: null,
      pinLabelTooltipComponent: null,
      pinLength: '4', // default "4", other custom values --> "5", "8"
      pinterestLink: '',
      pinTruncate: 'last4', // default "last4", other values --> "first4", "none"
      pinType: 'tel', // default "tel", other values --> "text"
      prefixConfig: {
        lengthWithPrefix: 0,
        prefixString: '',
        exemptLengths: []
      },
      resultsClassNames: 'light-grey-text',
      resultsExpiryText: 'Expiry',
      resultsHeadingClassNames: '',
      resultsHeadingText: 'Balance',
      resultsOrder: 'order-last',
      seperateResConfirmButton: null,
      seperateResultsMessage: null,
      seperateResultsMessageRowClass: null,
      seperateResultsMessageURL: null,
      showCardErrorMsgOnBlank: false,
      showCardEOV: true,
      showCardNumber: false,
      showFullCardNumber: false,
      showCardNumCounter: false,
      showCustomCardMessage: false,
      showEnhancedResults: false,
      showEnhancedStore: false,
      showFooter: false,
      showIcon: true,
      showLabels: true,
      showPageSizeOptions: true,
      showPinErrorMsgOnBlank: false,
      showPinNumCounter: false,
      showSeperateResultsView: false,
      showTimeZone: false,
      showTransactionTime: false,
      sidePanel: false,
      spinnerOrder: 'order-last',
      trnTableDateFormatLocaleOverride: null,
      twitterLink: '',
      withGiftCardFormContainerClassNames: 'd-col justify-content-center',
      withGiftCardMainClassNames: 'mx-auto',
      youtubeLink: '',
    };

    render() {
      const newProps = { ...this.props, ...PassedProps };

      /* Renders No Access Page if the page is supposed to be displayed solely within an iFrame and the page is currently not being hosted
         within an iFrame (only in production) */
      if (newProps.isIframe && (window.location === window.parent.location) && (process.env.NODE_ENV === 'production')) {
        return (
          <div id="noAccessPage">
            <h1>This page cannot be accessed here.</h1>
            <h4>Head to the {newProps.clientName !== '' ? newProps.clientName : 'corresponding company'} website to check your gift card balance.</h4>
          </div>
        );
      }
      // Renders page as normal otherwise
      else {
        return (
          <ClientConfig.Provider value={{ ...newProps }}>
            <PassedComponent />
          </ClientConfig.Provider>
        );
      }
    }
  }

  return WithTemplate;
};

export default withTemplate;
